import type { DeleteTaxonomyOrgMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DeleteTaxonomyOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

/**
 * Consumer input keeps the legacy `{ TaxonomyId, OrgKey }` shape. `OrgKey` is
 * only forwarded to the Apollo branch — the tRPC path derives the org from the
 * authenticated caller server-side and ignores any client-sent org.
 */
export interface DeleteTaxonomyOrgInput {
  TaxonomyId: string;
  OrgKey: string;
}

/**
 * Compound `deleteTaxonomyOrg` mutation (RSP-6488): deletes the org's
 * taxonomy_org row then the parent taxonomy iff orphaned.
 */
export const useDeleteTaxonomyOrg = createMutationHook({
  name: 'deleteTaxonomyOrg',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.taxonomy.deleteOrg.mutationOptions({ onSuccess }),
  mapInput: (input: DeleteTaxonomyOrgInput) => ({
    TaxonomyId: input.TaxonomyId,
  }),
  invalidate: (trpc) => [
    trpc.frontend.taxonomy.taxonomyAudit.queryKey(),
    trpc.frontend.taxonomy.taxonomyByLocale.queryKey(),
  ],
  // The data-layer performs both deletes in one transaction and returns the
  // `taxonomy_org` affected count; the parent-taxonomy count is not surfaced
  // separately, so it is reported as the same affected-row signal.
  mapResponse: (result: {
    affected_rows: number;
  }): DeleteTaxonomyOrgMutation => ({
    delete_taxonomy_org: {
      __typename: 'taxonomy_org_mutation_response',
      affected_rows: result.affected_rows,
    },
    delete_taxonomy: {
      __typename: 'taxonomy_mutation_response',
      affected_rows: result.affected_rows,
    },
  }),
  graphqlDocument: DeleteTaxonomyOrgDocument,
  graphqlVariables: (input: DeleteTaxonomyOrgInput) => ({
    TaxonomyId: input.TaxonomyId,
    OrgKey: input.OrgKey,
  }),
  evictFields: ['taxonomy', 'taxonomy_org', 'taxonomy_audit'],
});
